import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getProfilePhotoUrl } from '../../../utils/imageUtils';
import useLogout from '../../../hooks/useLogout';
import { getTestId } from '../../../utils/testUtils';
import { useNotifications, useTrainerExtendedFeatures } from '../../../utils/featureFlags';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Avatar,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  Notifications as NotificationsIcon,
  VerifiedUser as VerificationIcon,
  People as PeopleIcon,
  Label as LabelIcon,
  Assessment as QuotaIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  FitnessCenter as FitnessCenterIcon,
  Event as EventIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  BookmarkBorder as BookingIcon,
  Security as KvkkIcon,
  Person as PersonIcon,
  Class as ClassIcon,
  MonetizationOn as MoneyIcon,
  MenuBook as DiaryIcon,
  Group as GroupIcon,
  HowToReg as RegistrationIcon,
  CheckCircle as CheckIcon,
  Add as AddIcon,
  History as HistoryIcon,
  AcUnit as FreezeIcon
} from '@mui/icons-material';

const Navigation = ({ isOpen, onToggle, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const handleLogout = useLogout();
  const notificationsEnabled = useNotifications();
  const trainerExtendedFeaturesEnabled = useTrainerExtendedFeatures();

  const getNavigationItems = () => {
    // Calculate access based on registration status (same as backend logic)
    const canAccess = user?.registrationStatus === 'active' && user?.isActive;

    // Color mapping based on quick actions
    const getIconColor = (path) => {
      const colorMap = {
        '/admin/users': theme.palette.success.main,
        '/admin/facilities': theme.palette.warning.main,
        '/admin/scheduled-classes': theme.palette.info.main,
        '/admin/system-settings': theme.palette.info.main,
        '/notifications': theme.palette.error.main,
        '/admin/quotas': theme.palette.info.main,
        '/admin/member-types': theme.palette.success.main,
        '/admin/verification': theme.palette.warning.main,
        '/admin/plans': theme.palette.success.main,
        '/admin/plan-freezes': theme.palette.info.main,
        '/admin/payments': theme.palette.primary.main,
        '/admin/user-plan-editor': theme.palette.success.main,
        '/admin/trainer-payments': theme.palette.primary.main,
        '/dashboard': theme.palette.primary.main,
        '/my-plan-freezes': theme.palette.info.main,
        '/admin/bookings': theme.palette.secondary.main,
        '/admin/kvkk': theme.palette.error.main,
        '/admin/activity-logs': theme.palette.secondary.main,
        '/admin/class-types': theme.palette.info.main,
        '/trainer/profile': theme.palette.primary.main,
        '/trainer/classes': theme.palette.info.main,
        '/trainer/payments': theme.palette.success.main,
        '/sports-diary': theme.palette.secondary.main,
        '/facilities': theme.palette.warning.main,
        '/registration-status': theme.palette.warning.main,
        '/classes': theme.palette.info.main,
        '/my-bookings': theme.palette.secondary.main,
        '/plans': theme.palette.success.main,
        '/profile-settings': theme.palette.info.main,
        '/receptionist/bookings/create': theme.palette.success.main,
        '/receptionist/registration': theme.palette.primary.main,
        '/receptionist/payments': theme.palette.success.main,
        '/receptionist/members': theme.palette.primary.main
      };
      return colorMap[path] || theme.palette.text.secondary;
    };

    const commonItems = [
      {
        text: t('navigation:menu.dashboard'),
        icon: <DashboardIcon sx={{ color: getIconColor('/dashboard') }} />,
        path: '/dashboard',
      },
      ...(notificationsEnabled ? [
        {
          text: t('navigation:menu.notifications'),
          icon: <NotificationsIcon sx={{ color: getIconColor('/notifications') }} />,
          path: '/notifications',
        }
      ] : []),
    ];

    const roleSpecificItems = {
      admin: [
        {
          text: t('navigation:menu.admin.userVerification'),
          icon: <VerificationIcon sx={{ color: getIconColor('/admin/verification') }} />,
          path: '/admin/verification',
        },
        {
          text: t('navigation:menu.admin.userManagement'),
          icon: <PeopleIcon sx={{ color: getIconColor('/admin/users') }} />,
          path: '/admin/users',
        },
        {
          text: t('navigation:menu.admin.memberTypes'),
          icon: <LabelIcon sx={{ color: getIconColor('/admin/member-types') }} />,
          path: '/admin/member-types',
        },
        {
          text: t('navigation:menu.admin.quotaManagement'),
          icon: <QuotaIcon sx={{ color: getIconColor('/admin/quotas') }} />,
          path: '/admin/quotas',
        },
        {
          text: t('navigation:menu.admin.trainerPayments'),
          icon: <MoneyIcon sx={{ color: getIconColor('/admin/trainer-payments') }} />,
          path: '/admin/trainer-payments',
        },
        {
          text: t('navigation:menu.admin.paymentManagement'),
          icon: <PaymentIcon sx={{ color: getIconColor('/admin/payments') }} />,
          path: '/admin/payments',
        },
        {
          text: t('navigation:menu.admin.userPlanEditor'),
          icon: <SettingsIcon sx={{ color: getIconColor('/admin/user-plan-editor') }} />,
          path: '/admin/user-plan-editor',
        },
        {
          text: t('navigation:menu.admin.facilities'),
          icon: <FitnessCenterIcon sx={{ color: getIconColor('/admin/facilities') }} />,
          path: '/admin/facilities',
        },
        {
          text: t('navigation:menu.admin.classTypes'),
          icon: <EventIcon sx={{ color: getIconColor('/admin/class-types') }} />,
          path: '/admin/class-types',
        },
        {
          text: t('navigation:menu.admin.scheduledClasses'),
          icon: <ScheduleIcon sx={{ color: getIconColor('/admin/scheduled-classes') }} />,
          path: '/admin/scheduled-classes',
        },
        {
          text: t('navigation:menu.admin.plans'),
          icon: <CreditCardIcon sx={{ color: getIconColor('/admin/plans') }} />,
          path: '/admin/plans',
        },
        {
          text: t('navigation:menu.admin.planFreezes'),
          icon: <FreezeIcon sx={{ color: getIconColor('/admin/plan-freezes') }} />,
          path: '/admin/plan-freezes',
        },
        {
          text: t('navigation:menu.admin.systemSettings'),
          icon: <SettingsIcon sx={{ color: getIconColor('/admin/system-settings') }} />,
          path: '/admin/system-settings',
        },
        {
          text: t('navigation:menu.admin.bookingManagement'),
          icon: <BookingIcon sx={{ color: getIconColor('/admin/bookings') }} />,
          path: '/admin/bookings',
        },
        {
          text: t('navigation:menu.admin.kvkkManagement'),
          icon: <KvkkIcon sx={{ color: getIconColor('/admin/kvkk') }} />,
          path: '/admin/kvkk',
        },
        {
          text: t('navigation:menu.admin.activityLogs'),
          icon: <HistoryIcon sx={{ color: getIconColor('/admin/activity-logs') }} />,
          path: '/admin/activity-logs',
        },
      ],
      trainer: [
        {
          text: t('navigation:menu.trainer.myProfile'),
          icon: <PersonIcon sx={{ color: getIconColor('/trainer/profile') }} />,
          path: '/trainer/profile',
        },
        {
          text: t('navigation:menu.trainer.myClasses'),
          icon: <ClassIcon sx={{ color: getIconColor('/trainer/classes') }} />,
          path: '/trainer/classes',
        },
        ...(trainerExtendedFeaturesEnabled ? [
          {
            text: t('navigation:menu.trainer.paymentDashboard'),
            icon: <MoneyIcon sx={{ color: getIconColor('/trainer/payments') }} />,
            path: '/trainer/payments',
          },
          {
            text: t('navigation:menu.trainer.sportsDiary'),
            icon: <DiaryIcon sx={{ color: getIconColor('/sports-diary') }} />,
            path: '/sports-diary',
          },
          {
            text: t('navigation:menu.trainer.viewFacilities'),
            icon: <FitnessCenterIcon sx={{ color: getIconColor('/facilities') }} />,
            path: '/facilities',
          },
        ] : []),
      ],
      member: [
        ...(user?.registrationStatus !== 'active' ? [
          {
            text: t('navigation:menu.member.registrationStatus'),
            icon: <CheckIcon sx={{ color: getIconColor('/registration-status') }} />,
            path: '/registration-status',
          }
        ] : []),
        {
          text: t('navigation:menu.member.sportsDiary'),
          icon: <DiaryIcon sx={{ color: getIconColor('/sports-diary') }} />,
          path: '/sports-diary',
        },
        ...(canAccess ? [
          {
            text: t('navigation:menu.member.bookFacilities'),
            icon: <FitnessCenterIcon sx={{ color: getIconColor('/facilities') }} />,
            path: '/facilities',
          },
          {
            text: t('navigation:menu.member.browseClasses'),
            icon: <ClassIcon sx={{ color: getIconColor('/classes') }} />,
            path: '/classes',
          },
        ] : []),
        {
          text: t('navigation:menu.member.myBookings'),
          icon: <BookingIcon sx={{ color: getIconColor('/my-bookings') }} />,
          path: '/my-bookings',
        },
        {
          text: t('navigation:menu.member.plans'),
          icon: <CreditCardIcon sx={{ color: getIconColor('/plans') }} />,
          path: '/plans',
        },
        {
          text: t('navigation:menu.member.myFreezes'),
          icon: <FreezeIcon sx={{ color: getIconColor('/my-plan-freezes') }} />,
          path: '/my-plan-freezes',
        },
      ],
      receptionist: [
        {
          text: t('navigation:menu.receptionist.createBooking'),
          icon: <AddIcon sx={{ color: getIconColor('/receptionist/bookings/create') }} />,
          path: '/receptionist/bookings/create',
        },
        {
          text: t('navigation:menu.receptionist.memberRegistration'),
          icon: <RegistrationIcon sx={{ color: getIconColor('/receptionist/registration') }} />,
          path: '/receptionist/registration',
        },
        {
          text: t('navigation:menu.receptionist.processPayment'),
          icon: <MoneyIcon sx={{ color: getIconColor('/receptionist/payments') }} />,
          path: '/receptionist/payments',
        },
        {
          text: t('navigation:menu.receptionist.viewMembers'),
          icon: <GroupIcon sx={{ color: getIconColor('/receptionist/members') }} />,
          path: '/receptionist/members',
        },
        {
          text: t('navigation:menu.receptionist.userVerification'),
          icon: <VerificationIcon sx={{ color: getIconColor('/admin/verification') }} />,
          path: '/admin/verification',
        },
        {
          text: t('navigation:menu.receptionist.viewFacilities'),
          icon: <FitnessCenterIcon sx={{ color: getIconColor('/facilities') }} />,
          path: '/facilities',
        },
        {
          text: t('navigation:menu.receptionist.browseClasses'),
          icon: <ClassIcon sx={{ color: getIconColor('/classes') }} />,
          path: '/classes',
        },
      ],
    };

    return [...commonItems, ...(roleSpecificItems[user?.role] || roleSpecificItems.member)];
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile && onClose) {
      onClose();
    }
  };

  const onLogout = () => {
    if (isMobile && onClose) {
      onClose();
    }
    handleLogout();
  };

  const navigationItems = getNavigationItems();

  return (
    <>
      {/* Mobile Overlay - handled by MUI Drawer backdrop */}
      
      {/* Navigation Sidebar */}
      <Drawer
        {...getTestId('navigation-drawer')}
        variant={isMobile ? "temporary" : "persistent"}
        open={isOpen}
        onClose={onClose}
        sx={{
          width: { xs: '85%', sm: 280 },
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: { xs: '85%', sm: 280 },
            boxSizing: 'border-box',
            border: 'none',
            boxShadow: 3
          },
        }}
      >
        {/* Header */}
        <Box 
          {...getTestId('navigation-header')}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider'
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            
                              <Box sx={{ display: 'flex', justifyContent: 'center',mr:2 }}>
                                <Avatar 
                                  {...getTestId('navigation-logo')}
                                  src="/logo192.png" 
                                  alt="MetuMotion Logo"
                                  sx={{ 
                                    width: 64, 
                                    height: 64,
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                  }}
                                />
                              </Box>
            <Typography 
              {...getTestId('navigation-title')}
              variant="h6" 
              component="div"
            >
              MetuMotion
            </Typography>
          </Box>
          {isMobile && (
            <IconButton
              {...getTestId('navigation-close-button')}
              onClick={onClose}
              aria-label={t('navigation:actions.closeNavigation')}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        {/* User Info */}
        <Box 
          {...getTestId('navigation-user-info')}
          sx={{ 
            p: 2, 
            display: 'flex', 
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider'
          }}>
          <Avatar 
            {...getTestId('navigation-user-avatar')}
            src={getProfilePhotoUrl(user?.profilePhotoThumbnail || user?.profilePhoto)}
            sx={{ 
              bgcolor: 'primary.main',
              width: 40,
              height: 40,
              mr: 2
            }}
          >
            {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
          </Avatar>
          <Box>
            <Typography 
              {...getTestId('navigation-user-name')}
              variant="subtitle1"
            >
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography 
              {...getTestId('navigation-user-role')}
              variant="body2" 
              color="text.secondary" 
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Box component="span" sx={{ mr: 0.5 }}>
                {user?.role === 'admin' && '👑'}
                {user?.role === 'trainer' && '🏃'}
                {user?.role === 'receptionist' && '💼'}
                {user?.role === 'member' && '👤'}
              </Box>
              {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
            </Typography>
          </Box>
        </Box>

        {/* Navigation Items */}
        <List 
          {...getTestId('navigation-menu')}
          sx={{ flexGrow: 1, py: 0 }}
        >
          {navigationItems.map((item) => (
            <ListItem 
              key={item.text} 
              {...getTestId(`navigation-item-${item.text.toLowerCase().replace(/\s+/g, '-')}`)} 
              disablePadding
            >
              <ListItemButton 
                {...getTestId(`navigation-button-${item.text.toLowerCase().replace(/\s+/g, '-')}`)}
                onClick={() => handleNavigation(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  py: 1.5,
                  '&.Mui-selected': {
                    bgcolor: 'action.selected',
                    borderLeft: 4,
                    borderColor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    }
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Footer Actions */}
        <Box 
          {...getTestId('navigation-footer')}
          sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}
        >
          <Button
            {...getTestId('navigation-logout-button')}
            fullWidth
            variant="outlined"
            color="error"
            onClick={onLogout}
            startIcon={<CloseIcon />}
          >
            {t('navigation:menu.logout')}
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default Navigation;
